// SEO утилиты для галереи Валерии Вагнер

export interface ArtworkSEO {
  title: string;
  description: string;
  keywords: string[];
  price: number;
  category: string;
  medium: string;
  size: string;
}

export const generateArtworkSEO = (artwork: any): ArtworkSEO => {
  const baseKeywords = [
    'картина',
    'живопись',
    'художник Валерия Вагнер',
    'купить картину',
    'авторская работа',
    'Омск'
  ];

  // Словарь keywords синхронизирован с public/generate-seo.php
  const categoryKeywords: Record<string, string[]> = {
    'абстракция': ['абстрактная живопись', 'современное искусство', 'абстракция'],
    'пейзаж': ['пейзажная живопись', 'природа', 'пейзаж'],
    'натюрморт': ['натюрморт', 'предметы', 'композиция'],
    'животные': ['анималистика', 'животные', 'фауна'],
    'космос': ['космическая живопись', 'космос', 'вселенная'],
    'морской пейзаж': ['морская живопись', 'море', 'морской пейзаж'],
    'новый год': ['новогодние картины', 'праздничная живопись', 'зима'],
    'хэллоуин': ['хэллоуин', 'мистика', 'праздничные картины'],
    'мистика': ['мистическая живопись', 'таинственность', 'магия'],
    'сюрреализм': ['сюрреализм', 'фантазия', 'необычные образы'],
    'объемное панно': ['панно', 'рельеф', 'объемные работы'],
    'природа': ['природа', 'флора', 'растения']
  };

  // Словарь keywords синхронизирован с public/generate-seo.php
  const mediumKeywords: Record<string, string[]> = {
    'акрил': ['акриловые краски', 'акрил'],
    'масло': ['масляная живопись', 'масло'],
    'гипс': ['гипсовые работы', 'скульптура']
  };

  const keywords = [
    ...baseKeywords,
    ...(categoryKeywords[artwork.category as keyof typeof categoryKeywords] || []),
    ...(mediumKeywords[artwork.medium as keyof typeof mediumKeywords] || []),
    ...(artwork.tags || [])
  ];

  // Генерируем уникальный SEO-friendly title
  const sizeText = artwork.size ? `${artwork.size} см` : '';
  const mediumText = artwork.medium || '';
  
  let title = `${artwork.title}`;
  if (mediumText) {
    title += ` - ${mediumText}`;
  }
  if (sizeText) {
    title += ` ${sizeText}`;
  }
  title += ` | Валерия Вагнер`;
  
  // Ограничиваем title до 60 символов
  if (title.length > 60) {
    title = title.substring(0, 57) + '...';
  }
  
  // Генерация уникального SEO описания
  let description = artwork.seo_description;
  
  if (!description) {
    const parts = [];
    
    // Берем первые 100 символов из описания работы
    if (artwork.description) {
      parts.push(artwork.description.substring(0, 100));
    }
    
    // Добавляем технику или вдохновение
    if (artwork.technique) {
      parts.push(artwork.technique);
    } else if (artwork.inspiration) {
      parts.push(artwork.inspiration.substring(0, 50));
    }
    
    // Основная часть
    let mainDesc = parts.filter(p => p).join('. ');
    if (mainDesc) {
      mainDesc += '. ';
    }
    
    // Продающая часть
    const price = artwork.price ? artwork.price.toLocaleString('ru-RU') : '';
    mainDesc += `Купить картину "${artwork.title}" (${artwork.category}, ${artwork.medium}`;
    
    if (artwork.size) {
      mainDesc += `, ${artwork.size} см`;
    }
    
    mainDesc += ')';
    
    if (price) {
      mainDesc += ` за ${price} ₽`;
    }
    
    mainDesc += '. Авторская работа художника Валерии Вагнер из Омска. Доставка по России.';
    
    // Ограничиваем до 160 символов
    description = mainDesc.length > 160 ? mainDesc.substring(0, 157) + '...' : mainDesc;
  }

  return {
    title,
    description,
    keywords: [...new Set(keywords)],
    price: artwork.price,
    category: artwork.category,
    medium: artwork.medium,
    size: artwork.size
  };
};

export const generateSchemaMarkup = (artwork: any, seo: ArtworkSEO) => {
  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": artwork.title,
    "description": seo.description,
    "image": [artwork.image],
    "brand": {
      "@type": "Brand",
      "name": "Валерия Вагнер"
    },
    "creator": {
      "@type": "Person",
      "name": "Валерия Вагнер",
      "url": "https://w-wagner.art"
    },
    "category": artwork.category,
    "offers": {
      "@type": "Offer",
      "price": artwork.price,
      "priceCurrency": "RUB",
      "availability": artwork.status === "в наличии" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Художественная мастерская Валерии Вагнер",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Омск",
          "addressCountry": "RU"
        }
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Техника",
        "value": artwork.medium
      },
      {
        "@type": "PropertyValue", 
        "name": "Размер",
        "value": artwork.size + " см"
      },
      {
        "@type": "PropertyValue",
        "name": "Основа",
        "value": artwork.canvas
      }
    ]
  };
};